import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { Badge } from './Badge';

/**
 * StatusBadge Component
 *
 * Semantic badge component for displaying status with icons.
 * Maps common status states to appropriate badge variants and icons.
 *
 * @param {Object} props
 * @param {'active'|'inactive'|'pending'|'completed'|'in-progress'|'interested'|'not-interested'|'no-response'} props.status - Status type
 * @param {React.ReactNode} [props.children] - Custom content (overrides default status label)
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.showIcon=true] - Whether to show icon
 *
 * @example
 * <StatusBadge status="active" />
 * <StatusBadge status="pending">Custom Label</StatusBadge>
 * <StatusBadge status="completed" showIcon={false} />
 */
export function StatusBadge({ status, children, className = '', showIcon = true }) {
  const statusConfig = {
    active: {
      variant: 'status-active',
      icon: 'lucide:check-circle',
      defaultLabel: 'Active',
    },
    inactive: {
      variant: 'status-inactive',
      icon: 'lucide:x-circle',
      defaultLabel: 'Inactive',
    },
    pending: {
      variant: 'status-pending',
      icon: 'lucide:clock',
      defaultLabel: 'Pending',
    },
    completed: {
      variant: 'success',
      icon: 'lucide:check-circle-2',
      defaultLabel: 'Completed',
    },
    'in-progress': {
      variant: 'info',
      icon: 'lucide:loader',
      defaultLabel: 'In Progress',
    },
    interested: {
      variant: 'success',
      icon: 'lucide:check-circle',
      defaultLabel: 'Interested',
    },
    'not-interested': {
      variant: 'danger',
      icon: 'lucide:x-circle',
      defaultLabel: 'Not Interested',
    },
    'no-response': {
      variant: 'warning',
      icon: 'lucide:clock',
      defaultLabel: 'No Response',
    },
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <Badge variant={config.variant} className={className}>
      {showIcon && <Icon icon={config.icon} className="size-3" />}
      {children || config.defaultLabel}
    </Badge>
  );
}

StatusBadge.propTypes = {
  status: PropTypes.oneOf([
    'active',
    'inactive',
    'pending',
    'completed',
    'in-progress',
    'interested',
    'not-interested',
    'no-response',
  ]).isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
  showIcon: PropTypes.bool,
};
